import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNav } from '../../store/nav';
import { useActiveWallet } from '../../store/wallets';
import { useToast } from '../../store/toast';
import { useSession } from '../../hooks/useSession';
import { useBalances } from '../../hooks/useChain';
import { useCoinPrices } from '../../hooks/usePrices';
import { useSendTransfer } from '../../hooks/useSendTransfer';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { ApiError } from '../../lib/api';
import { feeQuoteApi } from '../../lib/transfers';
import { preloadTronWeb } from '../../lib/tron-sign';
import { groupInt, fmtUsd, shortAddr } from '../../lib/format';
import { CoinIcon } from '../CoinIcon';
import { FigIcon } from '../FigIcon';
import { ReauthGate } from './ReauthGate';
import { ProgressButton } from './ProgressButton';

const CONFETTI_COLORS = ['#2195f1', '#58b272', '#f7b32b', '#ec3240', '#8ccdff', '#8458db'];
const TRON_ADDRESS = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
/** Matches Config.feeFlatUsd schema default — used only until a quote returns. */
const FEE_FALLBACK_USD = 2;
type Step = 'form' | 'confirm' | 'auth' | 'sending' | 'success';

/** Send a coin. USDT is a gasless TRON disperse (server-quoted cost-plus fee);
 *  TRX is a native transfer where the sender pays their own bandwidth in TRX (no
 *  service fee). The seed is decrypted after reauth to sign the tx. */
const SEND_COINS = ['USDT', 'TRX'] as const;

export function SendScreen({ coin: initialCoin = 'USDT' }: { coin?: 'USDT' | 'TRX' }) {
  const { t } = useTranslation();
  const pop = useNav((s) => s.pop);
  const { token } = useSession();
  const showToast = useToast((s) => s.show);
  // Send from the wallet the user had open, not always the first one.
  const wallet = useActiveWallet();
  const from = wallet?.address ?? '';
  const index = wallet?.derivationIndex ?? 0;
  const balances = useBalances(from).data;
  // The coin is selectable in-screen (the amount field's badge is a picker), so
  // the route's coin is only the initial choice.
  const [coin, setCoin] = useState<'USDT' | 'TRX'>(initialCoin);
  const [coinPickerOpen, setCoinPickerOpen] = useState(false);
  const isTrx = coin === 'TRX';
  const price = useCoinPrices([coin]).data?.[coin]?.usd ?? (isTrx ? 0 : 1);
  // Sender's balance in the coin being sent (dev/browser: mock headroom).
  const chainBal = isTrx ? balances?.trx : balances?.usdt;
  const bal = token ? (chainBal ?? 0) : 1_000_000;
  // Keep a little TRX aside for the native transfer's bandwidth (burned when the
  // free daily allowance is short); the server enforces the same reserve.
  const TRX_RESERVE = 0.3;
  const { stage, send } = useSendTransfer();

  const [step, setStep] = useState<Step>('form');
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [feeUsd, setFeeUsd] = useState<number | null>(null);
  const [feeFallbackUsd, setFeeFallbackUsd] = useState(FEE_FALLBACK_USD);
  const [feeLoading, setFeeLoading] = useState(false);
  // TRX only: the network's own fee (bandwidth + activating a new recipient). We
  // charge nothing on TRX, but the user must still see what the chain takes.
  const [netFeeTrx, setNetFeeTrx] = useState<number | null>(null);
  const [activatesRecipient, setActivatesRecipient] = useState(false);
  const [txId, setTxId] = useState('');

  useEffect(() => {
    void preloadTronWeb(); // warm the tronweb bundle off the critical path
  }, []);

  const amountNum = useMemo(() => Number(amount.replace(',', '.')) || 0, [amount]);
  const amountUsd = amountNum * price;
  const addrValid = TRON_ADDRESS.test(address.trim());
  // USDT: debit = amount + our service fee (never treat a missing quote as $0).
  // TRX: debit = amount + the chain's own fee (paid in TRX) — we take no cut.
  const usdtFee = feeUsd ?? feeFallbackUsd;
  const total = isTrx ? amountNum + (netFeeTrx ?? 0) : amountNum + usdtFee;
  const over = total > bal;
  // Real sends wait for the quote: USDT for the service fee, TRX for the network
  // fee (it comes out of the same balance). Dev/browser (no token) never quotes.
  const feeReady = !token || !feeLoading;
  const canContinue = addrValid && amountNum > 0 && !over && feeReady;

  // Quote whenever a valid recipient is entered: the service fee for USDT, the
  // network fee for TRX (which also depends on the amount and on whether the
  // recipient still needs activating).
  useEffect(() => {
    if (!token || !addrValid || !from) {
      setFeeUsd(null);
      setNetFeeTrx(null);
      setActivatesRecipient(false);
      setFeeLoading(false);
      return;
    }
    let alive = true;
    setFeeUsd(null);
    setNetFeeTrx(null);
    setFeeLoading(true);
    const id = setTimeout(() => {
      // OwnKey-style: pass amount for USDT too — create uses exactDualPlan on the
      // real amount; quoting without it underpriced dual energy/fee.
      const quoteAmount = amountNum > 0 ? String(amountNum) : isTrx ? '1' : undefined;
      feeQuoteApi(coin, address.trim(), from, quoteAmount)
        .then((r) => {
          if (!alive) return;
          // Prefer chargeUsd (strict / same as create) over soft feeUsd.
          setFeeUsd(r.chargeUsd ?? r.feeUsd);
          if (r.feeFallbackUsd != null && r.feeFallbackUsd > 0) setFeeFallbackUsd(r.feeFallbackUsd);
          setNetFeeTrx(r.networkFeeTrx ?? null);
          setActivatesRecipient(!!r.activatesRecipient);
          setFeeLoading(false);
        })
        .catch(() => {
          if (!alive) return;
          // Never treat a failed quote as $0 — that let zero-balance sends through.
          setFeeFallbackUsd((fb) => {
            setFeeUsd(fb);
            return fb;
          });
          setNetFeeTrx(null);
          setActivatesRecipient(false);
          setFeeLoading(false);
        });
    }, 350);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [coin, isTrx, token, addrValid, address, from, amountNum]);

  useBackHandler(step === 'confirm', () => setStep('form'));
  useBackHandler(step === 'auth', () => setStep('confirm'));
  useBackHandler(step === 'sending', () => {}); // block back mid-send
  useBackHandler(step === 'success', () => pop());

  const paste = async () => {
    haptic();
    try {
      const text = await navigator.clipboard?.readText();
      if (text) setAddress(text.trim());
    } catch {
      /* clipboard unavailable */
    }
  };
  const setMax = () => {
    haptic();
    // Leave whatever the send itself will cost: the network fee (TRX) or our
    // service fee (USDT), so Max never lands on an unaffordable amount.
    setAmount(String(Math.max(0, isTrx ? bal - (netFeeTrx ?? TRX_RESERVE) : bal - usdtFee)));
  };
  const onAmount = (v: string) =>
    setAmount(v.replace(/[^\d.,]/g, '').replace(/[.,]/, '§').replace(/[.,]/g, '').replace('§', '.'));

  const mapError = (e: unknown): string => {
    const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : '';
    if (code === 'insufficient_balance') {
      const data = e instanceof ApiError ? e.data : undefined;
      const need = typeof data?.need === 'string' ? data.need : null;
      const have = typeof data?.have === 'string' ? data.have : null;
      const shortfall = typeof data?.shortfall === 'string' ? data.shortfall : null;
      if (need && have) return t('send.err.insufficient', { need, have });
      if (shortfall) return t('send.err.insufficientShort', { shortfall });
      return t('send.err.insufficient', { need: '—', have: '—' });
    }
    const map: Record<string, string> = {
      daily_limit_reached: t('send.err.limit'),
      sponsor_risk_limit: t('send.err.sponsorLimit'),
      cooldown: t('send.err.cooldown'),
      kill_switch: t('send.err.unavailable'),
      tron_disperse_unavailable: t('send.err.unavailable'),
      tron_send_unavailable: t('send.err.unavailable'),
      rpc_unavailable: t('send.err.network'),
      timeout: t('send.err.timeout'),
      ws_timeout: t('send.err.timeout'),
      ws_closed: t('send.err.timeout'),
      below_min_amount: t('send.err.minAmount'),
      invalid_recipient: t('send.err.invalidRecipient'),
      energy_rental_failed: t('send.err.rental'),
      rental_result_uncertain: t('send.err.rental'),
      resources_not_on_chain: t('send.err.rental'),
      broadcast_not_ready: t('send.err.rental'),
      transfer_in_progress: t('send.err.inProgress'),
      transfer_preparing: t('send.err.preparing'),
      signed_transaction_expired: t('send.err.signedExpired'),
    };
    return map[code] ?? t('send.err.generic');
  };

  // Reauth succeeded → run the whole send. Returns true so ReauthGate never shows
  // a "wrong PIN" state for a send-side failure (handled with a toast instead).
  const runSend = async (reauthToken?: string): Promise<boolean> => {
    setStep('sending');
    try {
      const { txHash } = await send({ walletId: wallet!.id, index, address: from, to: address.trim(), amount, coin, reauthToken });
      setTxId(txHash ?? '');
      haptic('heavy');
      setStep('success');
    } catch (e) {
      haptic();
      console.error('[send] failed at stage', stage, e);
      showToast(mapError(e));
      setStep('confirm');
    }
    return true;
  };

  const amountLabel = `${groupInt(amountNum)} ${coin}`;
  // What the send costs on top of the amount: the chain's fee for TRX (0 when the
  // free daily bandwidth covers it) or our service fee for USDT.
  const feeLabel = isTrx
    ? netFeeTrx == null
      ? '—'
      : netFeeTrx === 0
        ? t('send.feeFree')
        : `${netFeeTrx} TRX${activatesRecipient ? ` · ${t('send.activatesRecipient')}` : ''}`
    : feeUsd != null || feeFallbackUsd != null
      ? fmtUsd(usdtFee)
      : '—';

  // ── Reauth gate ──
  if (step === 'auth') {
    return (
      <ReauthGate
        onAuthed={runSend}
        bioTitle={t('send.stage.signing')}
        bioHint={t('security.sendUnlockHint')}
      />
    );
  }

  // ── Success ──
  if (step === 'success') {
    return <SuccessView amountLabel={amountLabel} address={address.trim()} txId={txId} onDone={pop} />;
  }

  // ── Confirm (button animates in place while sending — no gray overlay) ──
  if (step === 'confirm' || step === 'sending') {
    const sending = step === 'sending';
    return (
      <div className="push-enter flex min-h-full flex-col bg-surface px-4 pb-6 pt-4">
        <div className="flex h-9 items-center justify-center">
          <h1 className="text-[16px] font-medium text-ink">{t('send.confirmTitle')}</h1>
        </div>
        <div className="mt-10 flex flex-col items-center gap-3">
          <CoinIcon symbol={coin} size={56} />
          <p className="[font-family:'SF_Pro_Rounded',system-ui] text-[34px] font-semibold text-ink">{amountLabel}</p>
          <p className="text-[15px] text-muted">~{fmtUsd(amountUsd)}</p>
        </div>
        <div className="mt-8 flex flex-col rounded-[24px] bg-card px-4">
          <SummaryRow label={t('send.to')} value={shortAddr(address.trim(), 6, 6)} />
          <SummaryRow label={t('send.network')} value="TRON (TRC-20)" />
          <SummaryRow label={t('send.amount')} value={amountLabel} />
          <SummaryRow label={isTrx ? t('send.networkFee') : t('send.fee')} value={feeLabel} />
          <SummaryRow
            label={t('send.totalDebit')}
            value={isTrx ? `${groupInt(total)} TRX` : fmtUsd(total)}
            last
          />
        </div>
        <div className="flex-1" />
        <ProgressButton
          active={sending}
          disabled={sending}
          onClick={() => {
            if (sending) return;
            haptic();
            setStep('auth');
          }}
          className={`mt-6 flex h-[56px] w-full items-center justify-center gap-2 rounded-full border border-card bg-accent text-[15px] font-bold text-white transition-opacity ${
            sending ? 'pointer-events-none' : 'active:bg-accent-ink'
          }`}
        >
          {sending ? (
            <>
              <span className="size-5 animate-spin rounded-full border-2 border-card/40 border-t-white" />
              {t(`send.stage.${stage === 'idle' ? 'creating' : stage}`)}
            </>
          ) : (
            t('common.confirm')
          )}
        </ProgressButton>
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="page-enter flex min-h-full flex-col bg-surface px-4 pb-6 pt-4">
      <div className="flex h-9 items-center justify-center">
        <h1 className="text-[16px] font-medium text-ink">{t('send.title')}</h1>
      </div>

      <div className="mt-6 flex flex-1 flex-col gap-8">
        <div className="flex flex-col gap-3">
          <p className="px-4 text-[15px] font-medium text-muted">{t('send.recipient')}</p>
          <div className="flex h-[60px] items-center gap-3 rounded-[26px] bg-card px-4">
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t('send.addressPlaceholder')}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-muted"
            />
            <button onClick={paste} className="shrink-0 text-[16px] text-accent active:opacity-70">
              {t('common.paste')}
            </button>
          </div>
          {address.trim().length > 0 && !addrValid && (
            <p className="px-4 text-[13px] text-negative">{t('send.invalidAddress')}</p>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center px-4 text-[15px] font-medium">
            <p className="flex-1 text-muted">{t('send.amount')}</p>
            <button onClick={setMax} className="active:opacity-70">
              <span className="text-muted">{t('send.max')}</span> {groupInt(bal)} {coin}
            </button>
          </div>
          <div className={`flex h-[60px] items-center justify-between gap-2 rounded-[26px] bg-card py-4 pl-4 pr-3 ${over ? 'ring-1 ring-negative' : ''}`}>
            <input
              value={amount}
              onChange={(e) => onAmount(e.target.value)}
              inputMode="decimal"
              placeholder="0"
              className="[font-family:'SF_Pro_Rounded',system-ui] min-w-0 flex-1 bg-transparent text-[24px] font-medium text-ink outline-none placeholder:text-muted"
            />
            <div className="relative shrink-0">
              <button
                onClick={() => {
                  haptic();
                  setCoinPickerOpen((v) => !v);
                }}
                className="flex items-center gap-1 rounded-full bg-surface py-[6px] pl-[6px] pr-2 active:opacity-80"
              >
                <CoinIcon symbol={coin} size={24} />
                <span className="text-[15px] font-medium text-ink">{coin}</span>
                <FigIcon
                  name="visaChevron"
                  size={14}
                  className={`text-muted transition-transform ${coinPickerOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {coinPickerOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCoinPickerOpen(false)} />
                  <div className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[150px] overflow-hidden rounded-2xl bg-card py-1 shadow-[0_8px_28px_rgba(0,0,0,0.14)] ring-1 ring-border">
                    {SEND_COINS.map((c) => (
                      <button
                        key={c}
                        onClick={() => {
                          haptic();
                          setCoin(c);
                          setAmount('');
                          setCoinPickerOpen(false);
                        }}
                        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left active:bg-black/[0.04] ${c === coin ? 'bg-black/[0.03]' : ''}`}
                      >
                        <CoinIcon symbol={c} size={22} />
                        <span className="flex-1 text-[15px] font-medium text-ink">{c}</span>
                        {c === coin && <FigIcon name="pfCheck" size={16} className="text-accent" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center px-4 text-[15px] font-medium">
            <p className={over ? 'text-negative' : 'text-muted'}>
              {over ? t('send.insufficient') : `~${fmtUsd(amountUsd)}`}
            </p>
            <p className="flex flex-1 items-center justify-end gap-1.5 text-muted">
              <span>{isTrx ? t('send.networkFee') : t('send.fee')}</span>
              {feeLoading ? (
                <span className="inline-block size-[13px] animate-spin rounded-full border-[2px] border-border border-t-accent align-middle" />
              ) : (
                <span>{feeLabel}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={() => {
          if (!canContinue) return;
          haptic();
          setStep('confirm');
        }}
        disabled={!canContinue}
        className={`mt-6 flex h-[56px] w-full items-center justify-center rounded-full border border-card text-[15px] font-bold transition-colors ${
          canContinue ? 'bg-accent text-white active:bg-accent-ink' : 'bg-accent text-white/[0.44]'
        }`}
      >
        {t('common.continue')}
      </button>
    </div>
  );
}

function SummaryRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-4 ${last ? '' : 'border-b border-hairline'}`}>
      <span className="text-[15px] text-muted">{label}</span>
      <span className="text-[15px] font-medium text-ink">{value}</span>
    </div>
  );
}

function SuccessView({
  amountLabel,
  address,
  txId,
  onDone,
}: {
  amountLabel: string;
  address: string;
  txId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const pieces = useMemo(
    () =>
      Array.from({ length: 70 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        dur: 2.4 + Math.random() * 1.8,
        size: 6 + Math.random() * 8,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
        rot: (Math.random() > 0.5 ? 1 : -1) * (540 + Math.round(Math.random() * 360)),
      })),
    [],
  );
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const share = () => {
    haptic();
    const body = t('send.shareBody', { amount: amountLabel, coin: '', to: address, tx: txId }).replace('  ', ' ');
    if (canShare) void navigator.share({ text: body }).catch(() => {});
  };
  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-surface px-8 pb-8 pt-0">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {pieces.map((p, i) => (
          <span
            key={i}
            className="confetti-piece absolute rounded-[2px]"
            style={{
              left: `${p.left}%`,
              width: p.size,
              height: p.size * 0.45,
              backgroundColor: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.dur}s`,
              ['--rot' as never]: `${p.rot}deg`,
            }}
          />
        ))}
      </div>
      <div className="relative flex flex-1 flex-col items-center justify-center gap-5 text-center">
        <span className="grid size-[72px] place-items-center rounded-full bg-accent text-white">
          <FigIcon name="pfCheck" size={34} />
        </span>
        <h1 className="text-[24px] font-medium text-ink">{t('send.sentTitle')}</h1>
        <p className="max-w-[280px] text-[15px] leading-[1.44] text-muted">
          {t('send.sentDesc', { amount: amountLabel, coin: '' }).trim()} · {shortAddr(address, 6, 6)}
        </p>
      </div>
      <div className="relative flex flex-col gap-3">
        {canShare && (
          <button
            onClick={share}
            className="flex h-[56px] w-full items-center justify-center gap-2 rounded-full border border-card bg-accent text-[15px] font-bold text-white active:bg-accent-ink"
          >
            {t('send.share')}
          </button>
        )}
        <button
          onClick={onDone}
          className="flex h-[56px] w-full items-center justify-center rounded-full bg-card text-[15px] font-bold text-ink shadow-card active:opacity-80"
        >
          {t('send.done')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
